import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { FilterIcon, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { InspectionStatus, type InspectionType } from '@/common/inspection';
import type { Pagination } from '@/common/pagination';
import { ROLE } from '@/common/role';
import { DeletedViewBanner } from '@/components/soft-delete/DeletedViewBanner';
import { RestoreConfirmDialog } from '@/components/soft-delete/RestoreConfirmDialog';
import { DELETED_STATUS, StatusFilterSelect } from '@/components/soft-delete/StatusFilterSelect';
import { Button } from '@/components/ui/button';
import { usePermission } from '@/hooks/use-permission';
import { useRestoreItem } from '@/hooks/use-soft-delete';
import { cn, getInspectionStatusLabel } from '@/lib/utils';
import { useQueryListInspectionsOptions } from '@/queries/inspections/list-inspections';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';

import { AbsoluteLoader } from '../../absoluteLoader';
import { Restricted } from '../../Restricted';
import { type SortOption, SortSelect } from '../../sort-select';

import { InspectionCard } from './inspection-card';

export type InspectionListProps = {
	assetId: string;
};

// Values must use INSPECTION_SORT_FIELDS from the backend DTO. Status sorts in workflow order.
export const inspectionSortOptions = (t: TFunction): SortOption[] => [
	{ value: 'createdAt:desc', label: t('common.sort.newest') },
	{ value: 'createdAt:asc', label: t('common.sort.oldest') },
	{ value: 'startDate:desc', label: t('inspections.list.sortStartDateDesc') },
	{ value: 'startDate:asc', label: t('inspections.list.sortStartDateAsc') },
	{ value: 'title:asc', label: t('common.sort.titleAsc') },
	{ value: 'title:desc', label: t('common.sort.titleDesc') },
	{ value: 'status:asc', label: t('common.labels.status') },
	{ value: 'type:asc', label: t('common.labels.type') },
];
const DEFAULT_SORT = 'createdAt:desc';

export function InspectionList({ assetId }: InspectionListProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [pagination, _setPagination] = useState({
		limit: 100,
		offset: 0,
	} as Pagination);
	const [sort, setSort] = useState(DEFAULT_SORT);
	const canDelete = usePermission(ROLE.INSPECTIONS_DELETE);
	// Status filter (GH #331): a real inspection status, or "Deleted" for the deleted inspections
	const [statusFilter, setStatusFilter] = useState<string>('all');
	const deleted = canDelete && statusFilter === DELETED_STATUS;
	const status =
		statusFilter !== 'all' && statusFilter !== DELETED_STATUS ? (statusFilter as InspectionStatus) : undefined;
	const [inspectionToRestore, setInspectionToRestore] = useState<InspectionType | null>(null);
	const restoreInspection = useRestoreItem({ onRestored: () => setInspectionToRestore(null) });
	const {
		data: inspectionReponse,
		isLoading,
		isRefetching,
	} = useQuery({
		...useQueryListInspectionsOptions(assetId, {
			...pagination,
			sort,
			...(status && { status }),
			...(deleted && { deleted: true }),
		}),
		// Keep the current cards on screen while a new sort order loads.
		placeholderData: keepPreviousData,
	});
	const inspections = inspectionReponse?.inspections || [];

	const isLoadingOrRefetching = isLoading || isRefetching;

	return (
		<div className="flex-1 overflow-auto h-full">
			<div className="space-y-4">
				<div className="flex flex-wrap justify-between items-center gap-2 mb-4">
					<h3 className="font-medium">{t('inspections.list.title')}</h3>
					<div className="flex flex-wrap items-center gap-2">
						<StatusFilterSelect
							value={statusFilter}
							onValueChange={setStatusFilter}
							options={[
								{ value: 'all', label: t('common.states.all') },
								...Object.values(InspectionStatus).map((value) => ({
									value,
									label: getInspectionStatusLabel(value),
								})),
							]}
							canShowDeleted={canDelete}
							ariaLabel={t('common.deleted.statusFilter')}
							className="h-9"
						/>
						<SortSelect
							options={inspectionSortOptions(t)}
							value={sort}
							onChange={setSort}
							className="h-9 lg:min-h-9 lg:w-48"
						/>
						<Restricted to={ROLE.INSPECTIONS_CREATE}>
							<Button
								size="sm"
								className="h-9 rounded-md px-3 shadow-sm"
								onClick={() => navigate(`/assets/${assetId}/inspections/new`)}
							>
								<Plus className="h-4 w-4" />
								{t('inspections.list.new')}
							</Button>
						</Restricted>
					</div>
				</div>
				{deleted && <DeletedViewBanner />}
				<div className="space-y-4 relative h-full">
					{isLoadingOrRefetching && (
						<div className={cn({ 'min-h-100': !inspections.length })}>
							<AbsoluteLoader className={cn('rounded-lg', { 'bg-transparent': !inspections.length })} isLoading />
						</div>
					)}
					{inspections.map((inspection) => (
						<InspectionCard
							key={inspection.id}
							inspection={inspection}
							onRestore={deleted ? setInspectionToRestore : undefined}
						/>
					))}
					{!isLoadingOrRefetching && !inspections.length && (
						<div className="text-center py-12 bg-card rounded-lg border border-dashed text-card-foreground">
							<FilterIcon className="size-8 mx-auto mb-2 opacity-50" />
							<p>{deleted ? t('common.deleted.empty') : t('inspections.list.empty')}</p>
						</div>
					)}
					{inspectionToRestore && (
						<RestoreConfirmDialog
							open
							onOpenChange={(open) => !open && setInspectionToRestore(null)}
							target={softDeleteTargets.inspection(assetId, inspectionToRestore.id as string)}
							name={inspectionToRestore.title || t('common.entities.inspection', { count: 1 })}
							isRestoring={restoreInspection.isPending}
							onConfirm={() =>
								restoreInspection.mutate({
									target: softDeleteTargets.inspection(assetId, inspectionToRestore.id as string),
									name: inspectionToRestore.title || t('common.entities.inspection', { count: 1 }),
								})
							}
						/>
					)}
				</div>
			</div>
		</div>
	);
}
